import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { noop } from 'lodash'
import {
  Drawer,
  Loader,
  Button,
  Notification as CoreNotification,
  Text,
} from '@adc/ui-core'
import classNames from 'classnames/bind'
import {
  useApplicationContext,
  ApplicationContextShape,
} from '../auctionApplication/context'
import { clearAllNotifications } from '../auctionApplication/queries/applicationSessionInformation'
import { useInfiniteScroll } from '../../hooks'
import { BellNotificationPropType } from './propTypes'
import cls from './styles.scss'
const cx = classNames.bind(cls)

const DRAWER_ELM_ID = 'bell_notifications_drawer'
const TITLE_TEXT = 'Your Notifications'

const Notification = ({ notification, elmId, position }) => {
  const { title, message, color, rawHtml, timestamp, messageId } = notification

  return (
    <div className={cx('notification')}>
      <CoreNotification
        isVisible
        color={color}
        title={title}
        message={message}
        rawHtml={rawHtml}
        timestamp={timestamp}
        elmId={`${elmId}_${position}`}
        fluid
      >
        {messageId && <span data-message-id={`${messageId}`} />}
      </CoreNotification>
    </div>
  )
}

Notification.propTypes = {
  notification: BellNotificationPropType,
  elmId: PropTypes.string,
  position: PropTypes.number,
  timestamp: PropTypes.string,
}

const EmptyState = () => {
  return <Text>You have no notifications. Please check later.</Text>
}

const DrawerContent = ({ notifications = [], elmId, onScrollEnd }) => {
  const { isFetching, setIsFetching } = useInfiniteScroll(
    `${DRAWER_ELM_ID}_content`,
    fetchData,
    true,
    800
  )

  function fetchData() {
    return onScrollEnd().then(_ => setIsFetching(false))
  }

  const notificationKeyTemplate = (index, messageId) =>
    messageId ? `${index}-${messageId}` : index

  return (
    <div className={cx('content')}>
      {notifications.map((n, i) => (
        <Notification
          key={notificationKeyTemplate(i, n.messageId)}
          elmId={`dt_${elmId}`}
          notification={n}
          position={i}
        />
      ))}
      {!notifications.length && <EmptyState />}
      {isFetching && (
        <div>
          <Loader active dimmer />
        </div>
      )}
    </div>
  )
}

DrawerContent.propTypes = {
  elmId: PropTypes.string.isRequired,
  notifications: PropTypes.arrayOf(BellNotificationPropType),
  onScrollEnd: PropTypes.func,
}

// TODO: This should probably be called BellNotificationsDrawer
const BellNotification = ({
  onClose,
  isOpen,
  elmId,
  notifications,
  onScrollEnd = noop,
}) => {
  const [loading, setLoading] = useState(false)
  const { setNotifications } = useApplicationContext()

  const onClearClick = () => {
    setLoading(true)
    return clearAllNotifications().then(_ => {
      setNotifications(ApplicationContextShape.notifications)
      return setLoading(false)
    })
  }

  const title = (
    <div className={cx('title')}>
      <div>{TITLE_TEXT}</div>
      <Button
        size='small'
        className={cx('clear-btn')}
        onClick={onClearClick}
        disabled={notifications.length === 0}
      >
        Clear All
      </Button>
    </div>
  )

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title={title}
      infiniteScroll
      elmId={DRAWER_ELM_ID}
    >
      <DrawerContent
        notifications={notifications}
        elmId={elmId}
        onScrollEnd={onScrollEnd}
      />
      {loading && <Loader active dimmer />}
    </Drawer>
  )
}

BellNotification.propTypes = {
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  elmId: PropTypes.string.isRequired,
  notifications: PropTypes.arrayOf(BellNotificationPropType),
  onScrollEnd: PropTypes.func,
}

export default BellNotification
export { BellNotificationPropType }
